// Dominant color of a station icon, used by the "Station artwork" setting to tint the player and
// artwork tiles (like streaming apps do). The same algorithm runs natively in the mobile apps.

// Buckets pixels by hue, weighting saturated mid-tones. Near-white and near-black pixels are
// ignored (backgrounds, outlines); monochrome icons fall back to their average neutral tone.
// `pixels` is RGBA bytes (e.g. ImageData.data). Returns "#RRGGBB" or null.
export function dominantColor(pixels){
  const weight=new Array(13).fill(0),sums=Array.from({length:13},()=>[0,0,0]);let opaque=0;
  for(let i=0;i+3<pixels.length;i+=4){
    if(pixels[i+3]<128)continue;opaque++;
    const r=pixels[i]/255,g=pixels[i+1]/255,b=pixels[i+2]/255;
    const high=Math.max(r,g,b),low=Math.min(r,g,b),delta=high-low,light=(high+low)/2;
    if(light<.07||light>.93)continue;
    const sat=delta===0?0:delta/(1-Math.abs(2*light-1));
    let bucket=12,w=.25;
    if(sat>=.22){
      let hue=(high===r?(g-b)/delta:high===g?(b-r)/delta+2:(r-g)/delta+4)/6%1;if(hue<0)hue+=1;
      bucket=Math.floor(hue*12)%12;w=sat*(1.1-Math.abs(light-.5));
    }
    weight[bucket]+=w;sums[bucket][0]+=r*w;sums[bucket][1]+=g*w;sums[bucket][2]+=b*w;
  }
  if(!opaque)return null;
  let best=0;for(let i=1;i<12;i++)if(weight[i]>weight[best])best=i;
  const chosen=weight[best]>=opaque*.04?best:12,total=weight[chosen];
  if(total<=0)return null;
  const hex=v=>Math.min(255,Math.max(0,Math.round(v/total*255))).toString(16).padStart(2,'0').toUpperCase();
  return `#${hex(sums[chosen][0])}${hex(sums[chosen][1])}${hex(sums[chosen][2])}`;
}

function toHsl(hex){
  const value=parseInt(hex.slice(1),16),r=(value>>16&255)/255,g=(value>>8&255)/255,b=(value&255)/255;
  const high=Math.max(r,g,b),low=Math.min(r,g,b),light=(high+low)/2,delta=high-low;
  if(!delta)return {h:0,s:0,l:light};
  const s=delta/(1-Math.abs(2*light-1));
  const h=(((high===r?(g-b)/delta:high===g?(b-r)/delta+2:(r-g)/delta+4)*60)%360+360)%360;
  return {h,s,l:light};
}

function hsl(h,s,l,alpha=1){
  const a=s*Math.min(l,1-l),channel=n=>{const k=(n+h/30)%12;return Math.round(255*(l-a*Math.max(-1,Math.min(k-3,9-k,1))));};
  return `rgba(${channel(0)}, ${channel(8)}, ${channel(4)}, ${alpha})`;
}

// Surface colors derived from the icon color, dark enough for the theme's light text.
export function tintFromColor(hex){
  const {h,s}=toHsl(hex),sat=Math.min(s,.62);
  return {
    backdrop:hsl(h,sat,.3),
    surface:hsl(h,Math.min(sat,.5),.17),
    accent:hsl(h,Math.min(s,.8),.72),
    wash:hsl(h,Math.min(sat,.55),.24,.85),
    washClear:hsl(h,Math.min(sat,.55),.24,0),
  };
}
